import React, {
  FC, useMemo, useState, useEffect,
} from 'react';
import _ from 'lodash';
import { DropdownItem } from 'reactstrap';
import Isvg from 'react-inlinesvg';
import classnames from 'classnames';
import history from 'app/routes/history';
import IconModal from 'app/components/general/IconModal';
import Table from 'app/components/general/Table';
import ModalConfirm from 'app/components/general/ModalConfirm';
import Button from 'app/components/general/Button';
import ActionDots from 'app/components/general/ActionDots';
import PlusIcon from 'app/images/icons/plus.svg';
import DeleteIcon from 'app/images/icons/delete.svg';
import EditIcon from 'app/images/icons/edit.svg';
import StatusBadge from 'app/components/general/StatusBadge';
import ImportIcon from 'app/images/icons/upload.svg';

import { getTeamSummary } from 'app/ducks/teams/selectors';
import { ITeam } from 'app/ducks/teams/types';
import CreateTeam from 'app/components/teams/CreateTeam';

import ConnectIcon from 'app/images/icons/linked.svg';

interface IProps {
  actions: any;
  teamsById: ITeam[];
  teamIds: string[];
  teamId?: string;
  headerTitle?: string;
  appReady?: boolean;
  isLoading?: boolean;
  manual?: boolean;
  saveFilters?: boolean;
  showChildren?: boolean;
  initialFilterConfig?: any;
  persistentFilterConfig?: any;
  pagination?: any;
  match?: any;
  selectTeam: (value) => void;
}

const TeamsTable: FC<IProps> = (props: IProps = {
  manual: true,
  actions: undefined,
  teamsById: undefined,
  teamIds: undefined,
  initialFilterConfig: undefined,
  persistentFilterConfig: undefined,
  isLoading: false,
  showChildren: true,
  pagination: undefined,
  selectTeam: undefined,
}) => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [teamId, setTeamId] = useState(undefined);

  const filterOptions = [
    {
      label: 'Team Name',
      accessor: 'name',
      type: 'text',
    },
    {
      label: 'Season Name',
      accessor: 'seasonName',
      type: 'text',
    },
    {
      label: 'Status',
      accessor: 'collectionStatus',
      type: 'select',
      options: [
        { id: 'owing', name: 'Owing' },
        { id: 'overdue', name: 'Overdue' },
        { id: 'none', name: 'Up to Date' },
      ],
    },
  ];

  const fetchDataFromApi = ({
    sort, filter, pageNumber, pageSize,
  }): void => {
    const payload = {
      page: {
        size: pageSize,
        number: pageNumber,
      },
      sort,
      filter,
    };
    props.actions.getAllTeams(payload);
  };

  const handleOpenModal = (e, selectedTeamId): void => {
    e.preventDefault();
    setDeleteModalOpen(true);
    setTeamId(selectedTeamId);
  };

  const handeCloseModal = (e): void => {
    e.preventDefault();
    setDeleteModalOpen(false);
    setTeamId(undefined);
  };

  const handleOpenEditModal = (e, selectedTeamId): void => {
    e.preventDefault();
    props.actions.selectTeam(selectedTeamId);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = (e): void => {
    e.preventDefault();
    setEditModalOpen(false);
  };

  const handleOpenCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(true);
  };

  const handeCloseCreateModal = (e): void => {
    e.preventDefault();
    setCreateModalOpen(false);
  };

  const handleDelete = (e): void => {
    e.preventDefault();
    props.actions.deleteTeam({ id: teamId });
  };

  const renderDeleteModal = (): React.ReactNode => (
    <ModalConfirm
      title="Delete Team Member"
      icon={DeleteIcon}
      description="Are you sure you want to delete this team member?"
      buttonText="Delete"
      isOpen={deleteModalOpen}
      isLoading={props.isLoading}
      handleSubmit={(e) => handleDelete(e)}
      handleCancel={handeCloseModal}
    />
  );

  const handleClick = (id: string): void => {
    // props.actions.selectTeam(id);
    history.push(`/teams/detail/${id}`);
  };

  const data = useMemo(() => {
    if (_.isEmpty(props.teamsById) || props.teamIds === undefined) {
      return [];
    }
    const rows = _.reduce(
      props.teamIds,
      (obj, id) => {
        if (!_.isEmpty(props.teamsById[id])) {
          obj.push(getTeamSummary(props.teamsById[id]));
        }
        return obj;
      },
      [],
    );
    return rows;
  }, [props.teamsById, props.teamIds]);

  const handleImportTeamMembers = (): void => {
    history.push(
      {
        pathname: '/import/team-members',
        state: { modal: true, previousPath: props.match.url },
      },
    );
  };

  const statusBadges = (owingNumber, overdueNumber): React.ReactNode[] => {
    const statuses = [];

    if (owingNumber > 0) {
      statuses.push(
        <StatusBadge
          paymentText="owing"
          numberOfObjects={owingNumber}
        />,
      );
    }
    if (overdueNumber > 0) {
      statuses.push(
        <StatusBadge
          paymentText="overdue"
          numberOfObjects={overdueNumber}
        />,
      );
    }
    return statuses;
  };

  const renderName = (cellProps: {
    value: {
      name: string;
      connected: boolean;
    };
  }): React.ReactNode => (
    <div className="d-flex align-items-center">
      <span className="d-inline-block">
        {cellProps.value.name}
      </span>
      <Isvg src={ConnectIcon} className={classnames('connect-icon-list', { 'd-none': !cellProps.value.connected }, { 'd-inline-block': cellProps.value.connected })} />
    </div>
  );
  const renderStatus = (cellProps: {
    value: {
      owingNumber: number;
      overdueNumber: number;
    };
  }): React.ReactNode => (
    <div className="payment-badge-group">
      {statusBadges(cellProps.value.owingNumber, cellProps.value.overdueNumber)}
    </div>
  );

  const columns = useMemo(() => [
    {
      Header: 'Name',
      accessor: 'name',
      className: 'col',
      headerClassName: 'col',
      Cell: renderName,
    },
    {
      Header: 'Season',
      accessor: 'seasonName',
      className: 'col',
      headerClassName: 'col',
    },
    {
      id: 'status',
      Header: 'Collection Status',
      className: 'col flex-wrap',
      headerClassName: 'col flex-wrap',
      accessor: 'financialStatus',
      Cell: renderStatus,
      sortType: (rowA, rowB, id, desc): number => {
        // Overdue takes precedence
        const rowATally = (rowA.original.financialStatus.overdueNumber * 10) + rowA.original.financialStatus.owingNumber;
        const rowBTally = (rowB.original.financialStatus.overdueNumber * 10) + rowB.original.financialStatus.owingNumber;

        if (rowATally > rowBTally) return 1;
        if (rowBTally > rowATally) return -1;
        return 0;
      },
    },
    {
      Header: '# of Team Members',
      accessor: 'numberOfTeamMembers',
      className: 'col',
      headerClassName: 'col',
    },
  ], []);

  const counterText = props.pagination.meta.totalCount === 1 ? '1 team' : `${props.pagination.meta.totalCount || 0} teams`;

  return (
    <div>
      {renderDeleteModal()}
      <IconModal
        title="Create Team"
        icon={PlusIcon}
        handleCancel={handeCloseCreateModal}
        isOpen={createModalOpen}
      >
        <CreateTeam {...props} teamId={props.teamId} afterSubmit={handeCloseCreateModal} />
      </IconModal>
      <Table
        data={data}
        columns={columns}
        isLoading={props.isLoading}
        // aggressiveLoader
        headerTitle={props.headerTitle}
        objectTitle="teams"
        filterTitle="Teams Data"
        counterText={counterText}
        handleClick={handleClick}
        filterable
        saveFilters={props.saveFilters}
        manual={props.manual}
        fetchDataFromApi={fetchDataFromApi}
        filterOptions={filterOptions}
        filterConfig={props.initialFilterConfig}
        persistentFilterConfig={props.persistentFilterConfig}
        pagination={props.pagination}
        showPagination
      >
        {props.showChildren && (
          [<Button onClick={handleOpenCreateModal} noLoading className="btn-thinner btn-2">
            <Isvg src={PlusIcon} className="btn-icon" />
            New Team
          </Button>]
        )}
      </Table>
    </div>
  );
};

export default TeamsTable;
